import { InvalidDictionaryOutputScheme } from '../../errors'
import type { ExecutionContext } from '../context/execution-context'
import type { Extension } from '../extension'
import { ExtensionMethod } from '../method'
import type { SharedExtensionMethod, SharedExtensionParam } from '../model/model-types'
import { toProperties } from '../model/params-model'
import { MethodParams } from '../params'
import { ResultSpec } from '../result'
import type { DictionaryProps, DictionaryResult } from '../types'
import { type DictionaryPayload, DictionaryPayloadSchema } from './payload-schema'

// A dictionary — the dynamic option source behind a param (05-dictionaries.md). Invoked through the
// console's helper-method route while a user fills a form, and answers `{ items, cursor }`.

export class DictionaryMethod extends ExtensionMethod {
  readonly criteria: MethodParams
  readonly result: ResultSpec

  constructor(extension: Extension, private readonly props: DictionaryProps) {
    super(extension)

    this.criteria = MethodParams.of(props.criteria, {
      serviceId: extension.id,
      kind: 'dictionary',
      itemId: props.id,
      slot: 'criteria',
    })
    this.result = ResultSpec.from(props.result)
  }

  get id(): string {
    return this.props.id
  }

  get name(): string {
    return this.props.id
  }

  get registerAs(): string {
    return 'DICTIONARY'
  }

  // Optional on a dictionary (unlike an action), so `''` when undeclared. Also satisfies `LoaderOwner`, so a
  // generated loader inherits it the same way an action's does.
  get category(): string {
    return this.props.category ?? ''
  }

  get oauth2Scopes(): string[] {
    return this.props.oauth2Scopes ?? []
  }

  // The params a dictionary-bound field depends on — derived from the criteria, never hand-written.
  get dependsOn(): string[] {
    return this.criteria.keys()
  }

  // ── runtime ────────────────────────────────────────────────────────────────

  // Two-stage: the `{ search, cursor, criteria }` envelope the runtime owns, then the author-declared
  // criteria through the shared pipeline — reported as a criteria failure so the console can point at the
  // dependent fields rather than the whole call.
  validate(methodData: Record<string, unknown>): DictionaryPayload {
    const input = this.input(methodData)
    const payload = input.parseField<DictionaryPayload>('payload', DictionaryPayloadSchema)
    const criteria = this.criteria.normalize<Record<string, unknown>>(payload.criteria, input.invalidCriteria())

    return {
      search: payload.search,
      cursor: payload.cursor,
      criteria,
    }
  }

  // Authors may answer with the bare item list; the runtime contract is `{ cursor, items }`.
  async execute(payload: DictionaryPayload, ctx: ExecutionContext): Promise<DictionaryResult> {
    const result = await this.props.execute(ctx.bagWith(payload))

    if (Array.isArray(result)) {
      return { cursor: null, items: result } as unknown as DictionaryResult
    }

    if (result && typeof result === 'object' && Array.isArray(result.items)) {
      return { cursor: result.cursor, items: result.items }
    }

    throw new InvalidDictionaryOutputScheme(this.extension.id, this.name)
  }

  // ── output model ───────────────────────────────────────────────────────────

  toModel(): SharedExtensionMethod {
    const [colorStart, colorEnd] = this.extension.appearanceColors

    return {
      name: this.id,
      // Dictionaries are surfaced to the UI and to AI tools, so they need a human label. Fall back to the id.
      operationName: this.props.label ?? this.id,
      description: this.props.description || '',
      registerAs: this.registerAs,
      category: this.category,
      colorStart,
      colorEnd,
      customIcon: '',
      // A dictionary returns a `{ items, cursor }` OBJECT; default to OBJECT when no explicit sample is given.
      returnType: this.result.isDeclared ? this.result.returnType : 'OBJECT',
      queryParams: [],
      bodyParams: [this.#payloadParam()],
      // The `payload` wrapper's display label lives here (the bodyParam itself stays label-free, matching v1).
      metaInfo: {
        args: { payload: { label: 'Payload' } },
        ...this.result.toMetaInfo(this.id, this.criteria),
      },
    }
  }

  // Describe the full payload shape so the method advertises a complete, labelled input scheme (nested
  // props inline their own labels — mirroring the v1 jsdoc-parser layout).
  #payloadParam(): SharedExtensionParam {
    const properties: SharedExtensionParam[] = [
      { name: 'search', required: false, description: 'Optional search string to filter results.', type: 'STRING', label: 'Search' },
      { name: 'cursor', required: false, description: 'Pagination offset for retrieving the next page of results.', type: 'STRING', label: 'Cursor' },
    ]

    const criteriaProps = toProperties(this.criteria)

    if (criteriaProps.length) {
      properties.push({
        name: 'criteria',
        required: false,
        description: 'Current values of the params this dictionary depends on.',
        type: 'OBJECT',
        properties: criteriaProps,
      })
    }

    return {
      name: 'payload',
      required: false,
      description: 'Contains optional search string and pagination cursor.',
      type: 'OBJECT',
      properties,
    }
  }
}
